import type { CabinetOrderSpec } from '../data/cabinetPricing';
import type { DoorCatalogue } from '../data/doorCatalogue';
import type { PriceRequestInput } from '../data/priceRequests';
import type { HandleProduct } from '../doors/drilling';
import { BAY_KIND_LABEL, frontsFor, limitsKey, typeName, type Cabinet } from './model';
import type { CabinetOrderState } from './orderState';

/**
 * A made-to-measure cabinet order as a price request: one item per cabinet,
 * each described in full for the person pricing it and carrying the builder's
 * own spec, so the quote line is exactly the cabinet that was designed.
 *
 * <p>The description is the spec in words — sizes, build, inside, and every
 * front with its size — because the shop prices it by reading it (owner,
 * 2026-09-28: complexity cannot be judged automatically). It stays within the
 * 1,000 characters a quote line holds; the full spec is in `configuration`.
 */

/** The shop supplies no handles: the choice only sets how the fronts are drilled. */
const HANDLE_DRILLING: Record<HandleProduct, string> = {
  bar: 'drilled for bar handles',
  knob: 'drilled for knobs',
  cup: 'drilled for cup handles',
  none: 'no handle holes',
};
const DESCRIPTION_MAX = 1000;

export interface CabinetFinish {
  rangeName?: string;
  colourName?: string;
  handle: HandleProduct;
}

export function finishOf(spec: CabinetOrderSpec, catalogue: DoorCatalogue | undefined): CabinetFinish {
  const range = catalogue?.ranges().find((r) => r.id === spec.rangeId);
  const colour = catalogue?.colours().find((c) => c.id === spec.colourId);
  // Painted to order: the colour is the one the shopper named.
  const colourName = spec.colourId === 'ral' ? `painted to order in ${spec.paintColour?.trim() || 'a colour to confirm'}` : colour?.name;
  return { rangeName: range?.name, colourName, handle: spec.handle };
}

function size(c: Cabinet): string {
  if (c.type === 'stairs') return `${c.w} mm long, ${c.h} mm high falling to ${c.h2} mm, ${c.d} mm deep`;
  if (limitsKey(c.type, c.corner) === 'corner-l') return `${c.w} × ${c.b} mm L-shaped, ${c.h} mm high, ${c.d} mm deep`;
  return `${c.w} × ${c.h} × ${c.d} mm (W × H × D)`;
}

function inside(c: Cabinet): string[] {
  const parts: string[] = [];
  if (c.type === 'corner' && c.corner === 'blind') parts.push(`blind on the ${c.blind}`);
  if (c.type === 'stairs') {
    parts.push(`bays: ${c.bays.map((b) => `${BAY_KIND_LABEL[b.kind]}${b.kind === 'drawers' && b.n ? ` ×${b.n}` : ''} ${b.w} mm`).join(', ')}`);
    return parts;
  }
  if (c.type === 'drawers') {
    parts.push(`${c.drawerFronts.length} drawer${c.drawerFronts.length === 1 ? '' : 's'}, fronts ${c.drawerFronts.join(' / ')} mm top to bottom`);
    return parts;
  }
  if (c.doors > 0) parts.push(c.doors === 1 ? `1 door hinged ${c.hinge}` : `${c.doors} doors`);
  parts.push(c.shelves.length ? `shelves at ${c.shelves.join(', ')} mm from the bottom` : 'no shelves');
  return parts;
}

function fronts(c: Cabinet, finish: CabinetFinish): string {
  if (!c.fronts) return 'Carcass only, no fronts';
  const list = frontsFor(c).map((f) => `${f.qty}× ${f.kind} ${f.hLow ? `${f.h}→${f.hLow}` : f.h} × ${f.w} mm`);
  const range = [finish.rangeName, finish.colourName].filter(Boolean).join(', ');
  return `Fronts${range ? ` in ${range}` : ''}, ${HANDLE_DRILLING[finish.handle]}: ${list.join('; ')}`;
}

/** One cabinet in words, as the desk reads it. */
export function describeCabinet(c: Cabinet, finish: CabinetFinish): string {
  const text = [
    `${typeName(c)}, ${size(c)}`,
    `${c.build === 'rigid' ? 'Rigid' : 'Flat-pack'}, ${inside(c).join(', ')}`,
    fronts(c, finish),
  ].join('. ');
  return text.length <= DESCRIPTION_MAX ? text : `${text.slice(0, DESCRIPTION_MAX - 1)}…`;
}

/** The request the builder sends for its current order. */
export function cabinetPriceRequest(
  spec: CabinetOrderSpec,
  catalogue: DoorCatalogue | undefined,
  extra: { message?: string; phone?: string } = {},
): PriceRequestInput {
  const finish = finishOf(spec, catalogue);
  const pieces = spec.cabinets.reduce((sum, c) => sum + c.qty, 0);
  const first = spec.cabinets[0];
  const withFronts = spec.cabinets.some((c) => c.fronts);
  const range = [finish.rangeName, finish.colourName].filter(Boolean).join(' · ');
  return {
    topic: 'cabinets',
    title: spec.cabinets.length === 1 && first && first.qty === 1 ? `${typeName(first)}, made to measure` : `${pieces} made-to-measure cabinets`,
    summary: withFronts ? `${range || 'Fronts'} · ${HANDLE_DRILLING[finish.handle]}` : 'Carcasses only',
    message: extra.message?.trim() || undefined,
    phone: extra.phone?.trim() || undefined,
    items: spec.cabinets.map((c) => ({
      description: describeCabinet(c, finish),
      quantity: String(c.qty),
      configuration: {
        kind: 'made-to-measure-cabinet',
        rangeId: spec.rangeId,
        colourId: spec.colourId,
        ...(spec.colourId === 'ral' && spec.paintColour?.trim() ? { paintColour: spec.paintColour.trim() } : {}),
        handle: spec.handle,
        cabinet: c,
      },
    })),
  };
}

/**
 * A request a guest asked to send, kept across the trip to sign in and back.
 * Session storage: it is this tab's own, gone when the tab is, and holds only
 * the order they built and what they typed.
 */
export const PENDING_REQUEST_KEY = 'spatial.cabinets.price-request';

export interface PendingRequest {
  order: CabinetOrderState;
  message: string;
  phone: string;
}

export function keepPendingRequest(pending: PendingRequest): boolean {
  try {
    window.sessionStorage.setItem(PENDING_REQUEST_KEY, JSON.stringify(pending));
    return true;
  } catch {
    // Storage blocked: the shopper still goes to sign in, and builds the order again.
    return false;
  }
}

/** The kept request, once: reading it removes it, so it is sent at most once. */
export function takePendingRequest(): PendingRequest | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(PENDING_REQUEST_KEY);
    window.sessionStorage.removeItem(PENDING_REQUEST_KEY);
  } catch {
    return null;
  }
  return parsePendingRequest(raw);
}

export function parsePendingRequest(raw: string | null): PendingRequest | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingRequest> | null;
    const order = value?.order;
    if (!order || !Array.isArray(order.cabinets) || order.cabinets.length === 0 || typeof order.rangeId !== 'string') return null;
    return { order, message: typeof value.message === 'string' ? value.message : '', phone: typeof value.phone === 'string' ? value.phone : '' };
  } catch {
    return null;
  }
}
